import { DatePicker, DatePickerProps } from '@creditornot/cb-date-picker';
import { Calendar as CalendarIcon } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import DatePickerPopover from 'common/components/DatePickerPopover';
import TimePicker from 'common/components/weekly-schedules/TimePairInput/TimePicker';

const defaultLocale = typeof window === 'object' && window.navigator?.language ? window.navigator.language : 'en-GB';

const StyledPopover = styled(DatePickerPopover)`
  width: 100%;
`;

const StyledPickerWrapper = styled.div`
  padding: 4px 16px 20px;
`;

const StyledHr = styled.hr`
  height: 1px;
  background-color: ${cbSemanticCssVars.borderPrimary};
  border: none;
  margin: 0;
`;

const StyledTimePicker = styled(TimePicker)`
  margin: 10px 0 16px;
`;

const getDefaultTimeValue = (value?: DateTime) => {
  if (!value) {
    return 0;
  }

  return value.hour * 60 + value.minute;
};

const getDefaultDateValue = (timezone: string, value?: DateTime) => {
  if (!value) {
    return DateTime.fromJSDate(new Date()).setZone(timezone).toJSDate();
  }

  return value.setZone(timezone).toJSDate();
};

export type DateTimePickerProps = {
  className?: string;
  disabled?: boolean;
  locale?: string;
  value?: DateTime;
  timezone: string;
  disabledDays?: DatePickerProps['disabledDays'];
  isInvalid?: boolean;
  onBlur?(): void;
  onChange?(value?: DateTime): void;
};

type InputValue = { date: Date; time: number };
const DateTimePicker = ({
  className,
  disabled,
  locale = defaultLocale,
  value,
  timezone,
  disabledDays,
  isInvalid,
  onBlur,
  onChange,
}: DateTimePickerProps) => {
  const [inputValue, setInputValue] = useState<InputValue>({
    date: getDefaultDateValue(timezone, value),
    time: getDefaultTimeValue(value),
  });

  const formattedInputValue = useMemo(() => {
    const hour = Math.floor(inputValue.time / 60);
    const minute = inputValue.time % 60;

    return DateTime.fromJSDate(inputValue.date)
      .setZone(timezone)
      .set({ hour, minute })
      .toLocaleString(DateTime.DATETIME_SHORT, { locale });
  }, [inputValue, locale, timezone]);

  const handleInputChange = useCallback(
    (v: InputValue) => {
      const { year, month, day } = DateTime.fromJSDate(v.date);
      const hour = Math.floor(v.time / 60);
      const minute = v.time % 60;

      const newValue = DateTime.fromJSDate(v.date).setZone(timezone).set({
        year,
        month,
        day,
        hour,
        minute,
        second: 0,
        millisecond: 0,
      });

      onChange?.(newValue);
    },
    [onChange, timezone],
  );

  const handleDateChange = (date: Date) => {
    const newDate = DateTime.fromJSDate(date).startOf('day').setZone(timezone).toJSDate();
    setInputValue(prevState => {
      const newValue = {
        ...prevState,
        date: newDate,
      };

      handleInputChange(newValue);
      return newValue;
    });
  };

  const handleTimeChange = (time: number) => {
    setInputValue(prevState => {
      const newValue = {
        ...prevState,
        time,
      };

      handleInputChange(newValue);
      return newValue;
    });
  };

  const firstDayOfWeek = useMemo(() => {
    /**
     * FIXME: This one needs to be ignored for now (03/2023), as our current TS version
     * doesn't seem have types for Locale weekInfo.
     * It also has fallback to "1" as mozilla browsers don't support this API yet.
     * */
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const localizedFirstDay = new Intl.Locale(locale)?.weekInfo?.firstDay;
    if (localizedFirstDay >= 1 && localizedFirstDay <= 6) {
      return localizedFirstDay;
    }

    if (localizedFirstDay > 6) {
      return 0;
    }

    return 1;
  }, [locale]);

  return (
    <StyledPopover
      disabled={disabled}
      content={({ closePopover }) => (
        <StyledPickerWrapper data-testid="date-time-picker__popover">
          <StyledTimePicker
            value={inputValue.time}
            onChange={time => {
              handleTimeChange(time);

              closePopover();
            }}
          />
          <StyledHr />
          <DatePicker
            locale={locale}
            onChange={date => {
              if (date) {
                handleDateChange(date);
              }
              closePopover();
            }}
            value={inputValue.date}
            firstDayOfWeek={firstDayOfWeek}
            disabledDays={disabledDays}
            timezone={timezone}
          />
        </StyledPickerWrapper>
      )}
      onCloseStart={onBlur}
      contentZIndex={100002}
    >
      {({ openPopover }) => (
        <Input
          className={className}
          disabled={disabled}
          icon={<CalendarIcon />}
          onClick={openPopover}
          readOnly
          value={formattedInputValue}
          invalid={isInvalid}
        />
      )}
    </StyledPopover>
  );
};

export default DateTimePicker;
